import { useQuery } from '@tanstack/react-query';

import { queryOptions } from '~components/hooks/_constants';
import { useContractVaultManager, useContractVestaInterestManager } from '~components/hooks/contracts';
import { useGraphStatus } from '~components/hooks/useGraphStatus';
import { checkValidContractAddress, getContractAddress } from '~config/protocol';
import { ENativeAsset, EVestaAsset } from '~config/types';
import { queryClient } from '~pages/_app';
import { getChainUserVault } from '~services/contracts/troves';
import { GraphUserVault } from '~services/subgraph/vesta/types';
import { BIG_INT_ZERO } from '~utils/utils';

import { useVestaSubgraph } from '../../graph/useVestaSubgraph';
import { useSession } from '../../useSession';
import { DeterminedQueryResult, ETroveStatusGraph, UserVaultQuery } from '../_types';
import useAssetPrices from '../assets/useAssetPrices';

export const initialUserVaultState: GraphUserVault = {
  status: ETroveStatusGraph.NULL,
  collateral: 0,
  debt: 0,
  rawCollateral: BIG_INT_ZERO,
  rawDebt: BIG_INT_ZERO,
  rawUnpaidInterest: BIG_INT_ZERO,
  unpaidInterest: 0,
  accumulatedInterest: 0,
  rawAccumulatedInterest: BIG_INT_ZERO,
};

export const VaultUserDataQueryKey = 'vault-user-data';

const useVaultUserData = (asset: EVestaAsset): DeterminedQueryResult<UserVaultQuery> => {
  const {
    address: userAddress,
    isConnected,
    network: { networkId },
  } = useSession();
  const { data: prices, isLoading: isPricesLoading, isFetched: isPricesFetched } = useAssetPrices();
  const graph = useVestaSubgraph();
  const { data: isGraphFetchable } = useGraphStatus();
  const assetAddress = getContractAddress(asset, networkId);

  const queryKey = [
    VaultUserDataQueryKey,
    networkId,
    userAddress,
    asset,
    isGraphFetchable ? 'graph' : 'chain',
  ];

  const invalidate = () => queryClient.refetchQueries(queryKey);
  const { read: vaultManagerRead, address: vaultManagerAddress } = useContractVaultManager();
  const { read: interestManagerRead, address: interestManagerAddress } = useContractVestaInterestManager();

  const vaultUserData = useQuery(
    queryKey,
    async () => {
      try {
        return isGraphFetchable
          ? await graph.user.getUserVault(userAddress!, asset, networkId)
          : await getChainUserVault({
              assetAddress,
              userAddress: userAddress!,
              vaultManagerRead: vaultManagerRead,
              interestManagerRead: interestManagerRead,
            });
      } catch (err) {
        console.error(err);
      }
    },
    queryOptions(
      false,
      checkValidContractAddress([vaultManagerAddress, interestManagerAddress]) &&
        isConnected &&
        !isPricesLoading &&
        isPricesFetched,
    ),
  );

  const vaultUserDataWithInitial = { ...vaultUserData, data: vaultUserData.data ?? initialUserVaultState };

  return {
    ...vaultUserDataWithInitial,
    data: {
      vault: vaultUserDataWithInitial.data,
      computed: {
        collateralUsd: vaultUserDataWithInitial.data.collateral * prices.oracle[asset],
        debtUsd: vaultUserDataWithInitial.data.debt * prices.market[ENativeAsset.VST],
        collateralRatio:
          (vaultUserDataWithInitial.data.collateral * prices.oracle[asset]) /
          vaultUserDataWithInitial.data.debt,
      },
    } as UserVaultQuery,
    invalidate,
  } as DeterminedQueryResult<UserVaultQuery>;
};

export default useVaultUserData;
